import * as yup from "yup";
import { useFormik } from "formik";
import {
  Alert,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import PhoneInput from "react-phone-input-2";
import dateIcon from "../../../images/date.png";
import bagsIcon from "../../../images/suitcase.png";
import adultsIcon from "../../../images/couple.png";
import childsIcon from "../../../images/children.png";
import { TerminalGateFrom } from "../../../utils/TerminalGate";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AirplaneTicketIcon from "../../../images/tiketIcon.png";
import ScheduleField from "./ScheduleField";
import CustomDestinationField from "./CustomDestinationField";
import axios from "axios";
import dayjs from "dayjs";
import { scheduleSeatsUrl } from "../../../utils/ApiUrl";
import { ScheduleData } from "../../../utils/ScheduleDetail";

interface FormikFrom {
  editValue: any;
  editState: boolean;
  setIdSchedule: (x: any) => void;
}

const validationSchema = yup.object({
  name: yup.string().required("Name is Required"),
  email: yup.string().email().required("Email is Required"),
  from: yup.string().required("Please Select Pick Up Destinations"),
  to: yup.string().required("Please Select Destinations Address"),
  date: yup.date().required("Please Input Date"),
  time: yup.string().required("Please Input Time"),
  adults: yup.number().required().min(1, "Please Input Adults Amount"),
  childs: yup.number().typeError("Please Input 0 When No Child"),
  bags: yup.number().required().typeError("Please Input Bags Amount"),
  contactnumber: yup.string().min(8, "Must be exactly 8 digits").required(),
  flightnumber: yup.string(),
});

const FormikForm = (props: FormikFrom) => {
  var customParseFormat = require("dayjs/plugin/customParseFormat");
  dayjs.extend(customParseFormat);
  const { editValue, editState, setIdSchedule, ...rest } = props;
  const [seaterValue, setSeaterValue] = useState<number>(0);
  const [bagsValue, setBagsValue] = useState<number>(0);
  const [scheduleId, setScheduleId] = useState<number>(0);
  const [scheduleTime, setScheduleTime] = useState<string>("");
  const [disableDestination, setDisableDestination] = useState(true);
  const DateValidation = new Date(Date.now() + 3600 * 1000 * 12);

  let navigate = useNavigate();

  function editInitialValue() {
    if (editValue !== null) {
      formik.setFieldValue("from", editValue.from);
      formik.setFieldValue("email", editValue.email);
      formik.setFieldValue("name", editValue.name);
      formik.setFieldValue("to", editValue.to);
      formik.setFieldValue("hotelAddress", editValue.hotelAddress);
      formik.setFieldValue("date", new Date(editValue.date));
      formik.setFieldValue("time", editValue.time);
      formik.setFieldValue("adults", editValue.adults);
      formik.setFieldValue("childs", editValue.childs);
      formik.setFieldValue("bags", editValue.bags);
      formik.setFieldValue("contactnumber", editValue.contactnumber);
      formik.setFieldValue("flightnumber", editValue.flightnumber);
      setScheduleId(editValue.scheduleId);
      setDisableDestination(false);
    }
  }

  const formik = useFormik({
    initialValues: {
      isFromAirport: true,
      email: "",
      name: "",
      from: "",
      to: "",
      hotelAddress: "",
      date: new Date(Date.now()),
      time: "",
      adults: "",
      childs: "",
      bags: 0,
      contactnumber: "",
      flightnumber: "",
      scheduleId: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      localStorage.setItem("form-value", JSON.stringify(values));
      navigate("/BookingSummary/CityShuttle", { replace: true });
    },
  });

  const CheckSeat = () => {
    const { date, time } = formik.values;
    const formattedTime = dayjs(time, "HH:MM");

    const combinedDateAndTime = dayjs(date)
      .hour(formattedTime.hour())
      .minute(formattedTime.minute());

    if (dayjs(combinedDateAndTime).isBefore(DateValidation))
      return setSeaterValue(0);

    axios
      .request({
        method: "GET",
        url: `${scheduleSeatsUrl}`,
        params: {
          ScheduleId: scheduleId,
          PickUpTime: dayjs(formik.values.date).format("YYYY-MM-DD"),
        },
      })
      .then((res) => {
        if (scheduleTime === "01:00:00" || scheduleTime === "02:00:00") {
          setSeaterValue(0);
        } else {
          setSeaterValue(res.data.availableSeats);
        }
      })
      .catch((e) => {
        setSeaterValue(0);
        console.log(e);
      });
  };

  const CheckScheduleData = async () => {
    var scheduleData = await ScheduleData();
    scheduleData.bags && setBagsValue(scheduleData.bags);
  };

  useEffect(() => {
    if (scheduleId) {
      CheckSeat();
      CheckScheduleData();
    }

    formik.setFieldValue("scheduleId", scheduleId);
    formik.setFieldValue("adults", "");
    formik.setFieldValue("childs", "");
    formik.setFieldValue("bags", "");
    setIdSchedule(scheduleId);
  }, [scheduleId, formik.values.date]);

  useEffect(() => {
    editInitialValue();
  }, [editValue]);

  // Scroll to top when field error / empty
  useEffect(() => {
    const el = document.querySelector(".Mui-error, [data-error]");
    const field = document.getElementById("from");
    if (el?.parentElement) {
      (el?.parentElement ?? el)?.scrollIntoView({
        behavior: "auto",
        block: "center",
        inline: "center",
      });
    } else {
      (field?.parentElement ?? field)?.scrollIntoView({
        behavior: "auto",
        block: "center",
        inline: "center",
      });
    }
  }, [formik.isSubmitting]);

  return (
    <form onSubmit={formik.handleSubmit} id="formikForm">
      {scheduleId && seaterValue === 0 ? (
        <div className="mb-4">
          <Alert severity="error" id="errorMsg">
            There is not enough seats for this schedule, please choose a
            different schedule.{" "}
          </Alert>
        </div>
      ) : null}
      <div className="flex flex-wrap xl:space-x-14 space-x-0 md:jusitfy-between justify-center">
        <div className="flex flex-col max-w-[470px] w-[100%] xl:w-auto">
          <div className="formTextField max-h-[64px]">
            <a>Terminal Location*</a>
            <Select
              displayEmpty
              id="from"
              name="from"
              value={formik.values.from ?? ""}
              onChange={formik.handleChange}
              size="small"
              error={formik.touched.from && Boolean(formik.errors.from)}
            >
              <MenuItem disabled value="">
                <p className="text-[#a9a9a9]">Select Airport Pick Up</p>
              </MenuItem>
              {TerminalGateFrom.map((item) => (
                <MenuItem style={{ whiteSpace: "normal" }} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </div>
          <div className="flex space-x-10 md:space-x-7 items-center w-[100%]">
            <div className="formTextField">
              <a>Date Schedule*</a>
              <div className="formTextField row">
                <div className="iconField">
                  <img src={dateIcon} style={{ marginRight: "10px" }}></img>
                  <DatePicker
                    id="datePickup"
                    name="datePickup"
                    selected={formik.values.date}
                    onChange={(date) => formik.setFieldValue("date", date)}
                    minDate={new Date(Date.now() + 3600 * 1000)}
                    dateFormat="MMMM d, yyyy"
                    onKeyDown={(event) => {
                      event.preventDefault();
                    }}
                    className="flex border border-gray-300 hover:border-black w-[100%] py-[7px] pl-[10px] rounded"
                  />
                </div>
              </div>
            </div>
            <div className="formTextField">
              <a>Time Schedule*</a>
              <div className="iconField ">
                <ScheduleField
                  id={"time"}
                  name={"time"}
                  setInput={(text) => {
                    formik.setFieldValue("time", text);
                  }}
                  input={formik.values.time}
                  error={formik.touched.time && Boolean(formik.errors.time)}
                  helperText={formik.touched.time && formik.errors.time}
                  pickUpDate={formik.values.date}
                  setScheduleId={setScheduleId}
                  setScheduleTime={setScheduleTime}
                  setDisableDestination={setDisableDestination}
                />
              </div>
            </div>
          </div>
          <div className="formTextField">
            <div className="flex space-x-1 items-center">
              <div>Destination*</div>
              <div className="text-gray-400 text-sm">
                (Please Select Schedule)
              </div>
            </div>
            <CustomDestinationField
              id={"to"}
              name={"to"}
              setInput={(text) => {
                formik.setFieldValue("to", text);
              }}
              setHotelAddress={(address) => {
                formik.setFieldValue("hotelAddress", address);
              }}
              input={formik.values.to}
              error={formik.touched.to && Boolean(formik.errors.to)}
              helperText={formik.touched.to && formik.errors.to}
              scheduleId={scheduleId}
              disabled={disableDestination}
              editState={editValue && true}
            />
          </div>
          <div className="formTextField">
            <a>Contact No*</a>
            <PhoneInput
              country={"sg"}
              enableSearch
              autoFormat={false}
              value={formik.values.contactnumber ?? ""}
              onChange={(a, x, e) => formik.handleChange(e)}
              inputStyle={{
                width: "100%",
                height: "40px",
                borderColor: `${
                  formik.touched.contactnumber &&
                  Boolean(formik.errors.contactnumber)
                    ? "red"
                    : ""
                }`,
              }}
              dropdownStyle={{ padding: "0px 10px" }}
              inputProps={{
                id: "contactnumber",
                name: "contactnumber",
                required: true,
              }}
            />
          </div>
        </div>
        <div className="flex flex-col max-w-[475px]">
          <div className="formTextField">
            <a>Name*</a>
            <TextField
              id="name"
              name="name"
              placeholder="Name"
              value={formik.values.name}
              size="small"
              onChange={(event) => {
                formik.setFieldValue("name", event.target.value);
              }}
              error={formik.touched.name && Boolean(formik.errors.name)}
            />
          </div>
          <div className="formTextField">
            <a>Email*</a>
            <TextField
              id="email"
              name="email"
              size="small"
              placeholder="Email"
              value={formik.values.email}
              onChange={(event) => {
                formik.setFieldValue("email", event.target.value);
              }}
              error={formik.touched.email && Boolean(formik.errors.email)}
            />
          </div>
          <div className="flex space-x-5 md:space-x-5">
            <div className="formTextField">
              <a>Flight Number</a>
              <TextField
                id="flightnumber"
                name="flightnumber"
                placeholder="Flight Number"
                value={formik.values.flightnumber}
                size="small"
                onChange={formik.handleChange}
                InputProps={{
                  inputProps: {
                    min: 0,
                  },
                  startAdornment: (
                    <InputAdornment position="start">
                      <img
                        src={AirplaneTicketIcon}
                        style={{ marginRight: "10px", width: "25px" }}
                      ></img>
                    </InputAdornment>
                  ),
                }}
                error={
                  formik.touched.flightnumber &&
                  Boolean(formik.errors.flightnumber)
                }
                helperText={
                  formik.touched.flightnumber && formik.errors.flightnumber
                }
              />
            </div>
            <div className="formTextField">
              <div className="flex space-x-1 items-center">
                <div>Bags*</div>
                <div className="text-gray-400 text-sm">
                  (Max {bagsValue} Bags)
                </div>
              </div>

              <div className="formTextField row">
                <div className="iconField">
                  <TextField
                    id="bags"
                    name="bags"
                    size="small"
                    placeholder="No. Of Bags"
                    type="number"
                    value={formik.values.bags}
                    style={{ width: "100%" }}
                    InputProps={{
                      inputProps: {
                        min: 0,
                      },
                      startAdornment: (
                        <InputAdornment position="start">
                          <img
                            src={bagsIcon}
                            style={{ marginRight: "10px" }}
                          ></img>
                        </InputAdornment>
                      ),
                    }}
                    onChange={(event) => {
                      var value = parseInt(event.target.value, 10);

                      if (value > bagsValue) value = bagsValue;

                      formik.setFieldValue("bags", value);
                    }}
                    error={formik.touched.bags && Boolean(formik.errors.bags)}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="formTextField">
            <div className="flex space-x-1 items-center">
              <div>Guest*</div>
              <div className="text-gray-400 text-sm">
                ({seaterValue ? seaterValue : "0"} Seats Left)
              </div>
            </div>
            <div className="flex justify-between md:space-x-5 md:space-y-0 space-y-0 space-x-5">
              <div className="iconField max-w-[374px] ">
                <TextField
                  id="adults"
                  name="adults"
                  placeholder="No. Of Adults"
                  size="small"
                  type="number"
                  style={{ width: "100%" }}
                  InputProps={{
                    inputProps: {
                      min: 0,
                    },
                    startAdornment: (
                      <InputAdornment position="start">
                        <img
                          src={adultsIcon}
                          className="w-[25px]"
                          style={{ marginRight: "10px" }}
                        ></img>
                      </InputAdornment>
                    ),
                  }}
                  value={formik.values.adults}
                  onChange={(event) => {
                    var value = parseInt(event.target.value, 10);
                    var formikValueChilds = parseInt(formik.values.childs, 10);
                    if (
                      value + formikValueChilds > seaterValue ||
                      value > seaterValue
                    )
                      value = seaterValue - formikValueChilds;
                    formik.setFieldValue("adults", value);
                  }}
                  error={formik.touched.adults && Boolean(formik.errors.adults)}
                />
              </div>
              <div className="iconField max-w-[374px]">
                <TextField
                  id="childs"
                  name="childs"
                  placeholder="No. Of Childs"
                  type="number"
                  size="small"
                  style={{ width: "100%" }}
                  InputProps={{
                    inputProps: {
                      min: 0,
                    },
                    startAdornment: (
                      <InputAdornment position="start">
                        <img
                          src={childsIcon}
                          className="w-[25px]"
                          style={{ marginRight: "10px" }}
                        ></img>
                      </InputAdornment>
                    ),
                  }}
                  value={formik.values.childs}
                  onChange={(event) => {
                    var value = parseInt(event.target.value, 10);
                    var formikValueAdulst = parseInt(formik.values.adults, 10);
                    if (
                      value + formikValueAdulst > seaterValue ||
                      value > seaterValue
                    )
                      value = seaterValue - formikValueAdulst;
                    formik.setFieldValue("childs", value);
                  }}
                  error={formik.touched.childs && Boolean(formik.errors.childs)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default FormikForm;
